// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { PenLine, RefreshCw, CheckCircle, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SignDocumentModal({ open, onOpenChange, file, onSuccess }) {
  const [position, setPosition] = useState('bottom-right');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSign = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/documents/${file.id}/sign`, {
        file_id: file.id,
        position,
        comment: comment.trim() || null
      });
      
      toast.success('Documento rubricado com sucesso!');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao rubricar documento';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const isPDF = file?.content_type === 'application/pdf';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <PenLine className="w-5 h-5 text-primary" />
            Rubricar Documento
          </DialogTitle>
          <DialogDescription>
            Adicione sua rubrica a este documento para validação
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* File info */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{file?.original_filename}</p>
              <p className="text-xs text-muted-foreground">
                {isPDF ? 'A rubrica será adicionada ao PDF' : 'Rubrica registrada no sistema'}
              </p>
            </div>
          </div>
          
          {/* Position selector (only for PDFs) */}
          {isPDF && (
            <div className="space-y-2">
              <Label>Posição da rubrica no documento</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione a posição" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom-right">Inferior direito</SelectItem>
                  <SelectItem value="bottom-left">Inferior esquerdo</SelectItem>
                  <SelectItem value="top-right">Superior direito</SelectItem>
                  <SelectItem value="top-left">Superior esquerdo</SelectItem>
                  <SelectItem value="center">Centro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Optional comment */}
          <div className="space-y-2">
            <Label>Observação (opcional)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Adicione uma observação sobre esta rubrica..."
              rows={2}
              maxLength={200}
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSign} disabled={loading}>
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Confirmar Rubrica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DocumentSignaturesModal({ open, onOpenChange, file, signatures }) {
  if (!signatures) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Rubricas do Documento</DialogTitle>
          <DialogDescription>
            {file?.original_filename}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 max-h-[300px] overflow-y-auto py-4">
          {signatures.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma rubrica registrada
            </p>
          ) : (
            signatures.map((sig) => (
              <div key={sig.id} className="flex items-start gap-3 p-3 bg-muted rounded-xl">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{sig.signed_by_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(sig.signed_at).toLocaleString('pt-BR')}
                  </p>
                  {sig.comment && (
                    <p className="text-xs text-muted-foreground mt-1 italic">
                      "{sig.comment}"
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
